import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import PhotoGallery from './pages/PhotoGallery';
import PhotoResult from './components/PhotoResult';
import CameraSession from './pages/CameraSession';
import CameraResults from './pages/CameraResults';
import SelectedResults from './pages/SelectedResults';
import FinalOutput from './pages/FinalOutput';
import FinalOutput2 from './pages/FinalOutput2';
import RedeemCode from './pages/RedeemCode';
import ThankYouEmail from './pages/ThankYouEmail';
import FindSessionByEmail from './pages/FindSessionByEmail';
import AdminRedeemCodes from './pages/AdminRedeemCodes';
import AdminPhotoSessions from './pages/AdminPhotoSessions';
import AdminNavBar from './pages/AdminNavBar';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';

const adminPaths = ['/workspace', '/admin/redeem-codes', '/admin/photo-sessions'];

const Navigation = () => {
  const location = useLocation();
  const [countdown, setCountdown] = useState(120); // 2 minutes = 120 seconds
  const navigate = useNavigate();
  // Hanya tampilkan timer di halaman /frames
  const showTimer = location.pathname.startsWith('/frames');
  // Tampilkan nav-title + AdminNavBar hanya di halaman admin
  const showAdminNav = adminPaths.includes(location.pathname);

  // Reset timer setiap masuk ke /frames
  useEffect(() => {
    if (showTimer) setCountdown(120);
  }, [showTimer]);

  // Countdown timer effect hanya aktif di /frames
  useEffect(() => {
    if (!showTimer) return;
    if (countdown <= 0) {
      // Auto select frame ID 1 and redirect to camera session
      const sessionId = `session_${Date.now()}`;
      const sessionFrames = JSON.parse(localStorage.getItem('sessionFrames') || '{}');
      sessionFrames[sessionId] = 1; // Frame ID 1
      localStorage.setItem('sessionFrames', JSON.stringify(sessionFrames));
      localStorage.setItem('currentSessionId', sessionId);
      navigate('/camera-session');
      return;
    }
    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, navigate, showTimer]);

  // Format countdown to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Load cursive font for the decorative title
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(fontLink);

    // Add wave animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(fontLink)) {
        document.head.removeChild(fontLink);
      }
    };
  }, []);

  return (
    <nav className="app-navigation">
      <div className="nav-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Flex row: judul di kiri (flex:1), menu bar di kanan */}
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <h1 className="nav-title" style={{
            fontFamily: `'Great Vibes', cursive`,
            fontSize: '40px',
            lineHeight: 1.1,
            letterSpacing: '1px',
            color: 'rgb(245 221 255)',
            textShadow: '0 1px 0 rgba(0, 0, 0, 0.6)',
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {Array.from(' - Lavio Paperie - ').map((ch, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  animation: 'titleWave 2.2s ease-in-out infinite',
                  animationDelay: `${i * 0.06}s`,
                  willChange: 'transform'
                }}
              >
                  {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))}
          </h1>
        </div>
        {showAdminNav && <div style={{ flexShrink: 0 }}><AdminNavBar active={location.pathname} /></div>}
        <div className="nav-links" style={{ marginLeft: 24 }}>
          {showTimer && (
            <div style={{
              color: '#ffffff',
              fontSize: '24px',
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              fontFamily: 'monospace',
              background: countdown <= 30 ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              padding: '8px 16px',
              borderRadius: '8px',
              border: countdown <= 30 ? '2px solid #ff4444' : '2px solid rgba(255,255,255,0.3)',
              transition: 'all 0.3s ease'
            }}>
              {formatTime(countdown)}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

const AppWrapper = () => (
  <>
    <Navigation />
    <App />
  </>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<Navigate to="/redeem" replace />} />
      <Route path="/*" element={<AppWrapper />} />
      <Route path="/camera-session" element={<CameraSession />} />
      <Route path="/camera-results/:sessionId" element={<CameraResults />} />
      <Route path="/selected-results/:sessionId" element={<SelectedResults />} />
      <Route path="/final-output/:sessionId" element={<FinalOutput />} />
      <Route path="/final-output2/:sessionId" element={<FinalOutput2 />} />
      <Route path="/gallery/:name" element={<PhotoGallery />} />
      <Route path="/preview/:sessionId" element={<PhotoResult />} />
      <Route path="/redeem" element={<RedeemCode />} />
      <Route path="/thank-you-email/:sessionId" element={<ThankYouEmail />} />
      <Route path="/find-session" element={<FindSessionByEmail />} />
      <Route path="/admin/redeem-codes" element={<AdminRedeemCodes />} />
      <Route path="/admin/photo-sessions" element={<AdminPhotoSessions />} />
    </Routes>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
